import React, { useState, useEffect } from 'react';
import { Trash2, Download, Eye, RefreshCw, AlertCircle, Home, Upload, Image as ImageIcon } from 'lucide-react';

interface FileData {
  fileName: string;
  originalFileName: string;
  fileType: 'image' | 'video';
  mimeType: string;
  fileSize: number;
  uploadedAt: string;
}

interface Submission {
  _id: string;
  userName: string;
  userEmail: string;
  description: string;
  files: FileData[];
  tags: string[];
  createdAt: string;
}

interface AlbumPhoto {
  fileName: string;
  fileSize: number;
  uploadedAt: string;
}

type AlbumName = 'Games' | 'Memories' | 'Nature';

export default function AdminDashboard() {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSubmission, setExpandedSubmission] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  
  // Photo management state
  const [activeTab, setActiveTab] = useState<'submissions' | 'photos'>('submissions');
  const [albumPhotos, setAlbumPhotos] = useState<{ [key in AlbumName]: AlbumPhoto[] }>({
    Games: [],
    Memories: [],
    Nature: [],
  });
  const [selectedAlbum, setSelectedAlbum] = useState<AlbumName>('Games');
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState<string | null>(null);

  const fetchSubmissions = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await fetch('/api/submissions?page=1&limit=100');
      
      if (!response.ok) {
        throw new Error('Failed to fetch submissions');
      }

      const data = await response.json();
      setSubmissions(data.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load submissions');
      console.error('Error fetching submissions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAlbumPhotos = async (album: AlbumName) => {
    try {
      const response = await fetch(`/api/albums/${album}/photos`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch ${album} photos`);
      }

      const data = await response.json();
      setAlbumPhotos(prev => ({
        ...prev,
        [album]: data.data.photos,
      }));
    } catch (err: any) {
      console.error(`Error fetching ${album} photos:`, err);
    }
  };

  const fetchAllAlbumPhotos = async () => {
    await Promise.all([
      fetchAlbumPhotos('Games'),
      fetchAlbumPhotos('Memories'),
      fetchAlbumPhotos('Nature'),
    ]);
  };

  useEffect(() => {
    fetchSubmissions();
    fetchAllAlbumPhotos();
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(() => {
      fetchSubmissions();
      if (activeTab === 'photos') {
        fetchAllAlbumPhotos();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [activeTab]);

  const getFileUrl = (fileName: string) => {
    return `/api/preview/${fileName}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleDelete = async (submissionId: string) => {
    if (!window.confirm('Are you sure you want to delete this submission? This cannot be undone.')) {
      return;
    }

    setDeletingId(submissionId);
    try {
      const response = await fetch(`/api/submissions/${submissionId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete submission');
      }

      setSubmissions(submissions.filter(s => s._id !== submissionId));
    } catch (err: any) {
      setError(err.message || 'Failed to delete submission');
    } finally {
      setDeletingId(null);
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingPhotos(true);
    setError('');

    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('photos', files[i]);
      }

      const response = await fetch(`/api/albums/${selectedAlbum}/photos`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload photos');
      }

      const data = await response.json();
      
      // Refresh album photos
      await fetchAlbumPhotos(selectedAlbum);
      
      // Reset file input
      event.target.value = '';
      
      alert(`Successfully uploaded ${data.data.uploadedFiles.length} photo(s) to ${selectedAlbum}!`);
    } catch (err: any) {
      setError(err.message || 'Failed to upload photos');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handlePhotoDelete = async (album: AlbumName, fileName: string) => {
    if (!window.confirm('Are you sure you want to delete this photo? This cannot be undone.')) {
      return;
    }

    setDeletingPhoto(fileName);
    try {
      const response = await fetch(`/api/albums/${album}/photos/${fileName}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete photo');
      }

      // Refresh album photos
      await fetchAlbumPhotos(album);
    } catch (err: any) {
      setError(err.message || 'Failed to delete photo');
    } finally {
      setDeletingPhoto(null);
    }
  };

  if (isLoading && submissions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Admin Dashboard</h1>
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <a
              href="/"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition"
              title="Back to Home"
            >
              <Home className="w-5 h-5" />
              <span className="hidden sm:inline">Home</span>
            </a>
            <div className="border-l border-gray-300 h-6"></div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              {activeTab === 'submissions' && (
                <p className="text-gray-600 mt-2">Total Submissions: {submissions.length}</p>
              )}
              {activeTab === 'photos' && (
                <p className="text-gray-600 mt-2">
                  {selectedAlbum}: {albumPhotos[selectedAlbum].length} photo(s)
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => {
              fetchSubmissions();
              fetchAllAlbumPhotos();
            }}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('submissions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition ${
                activeTab === 'submissions'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Community Submissions
            </button>
            <button
              onClick={() => setActiveTab('photos')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition ${
                activeTab === 'photos'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Gallery Albums
            </button>
          </nav>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Submissions Tab */}
        {activeTab === 'submissions' && (
          <>
            {/* Empty State */}
            {submissions.length === 0 && !isLoading && (
              <div className="text-center py-16 bg-white rounded-lg shadow">
                <p className="text-lg text-gray-600 mb-4">
                  No submissions yet. Files uploaded by users will appear here.
                </p>
              </div>
            )}

            {/* Submissions List */}
            {submissions.length > 0 && (
              <div className="space-y-4">
                {submissions.map((submission) => (
              <div
                key={submission._id}
                className="bg-white rounded-lg shadow-md overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Submission ID: {submission._id.substring(0, 8)}...
                      </h3>
                      <p className="text-sm text-gray-500">
                        Uploaded: {formatDate(submission.createdAt)}
                      </p>
                      {submission.userName !== 'Anonymous' && (
                        <p className="text-sm text-gray-600 mt-1">
                          By: {submission.userName} ({submission.userEmail})
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          setExpandedSubmission(
                            expandedSubmission === submission._id ? null : submission._id
                          )
                        }
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {expandedSubmission === submission._id ? 'Collapse' : 'Expand'}
                      </button>
                      <button
                        onClick={() => handleDelete(submission._id)}
                        disabled={deletingId === submission._id}
                        className="text-red-600 hover:text-red-700 font-medium disabled:opacity-50 flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        {deletingId === submission._id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>

                  {/* Files Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
                    {submission.files.map((file, idx) => (
                      <div
                        key={idx}
                        className="relative group rounded-lg overflow-hidden bg-gray-100"
                      >
                        {file.fileType === 'image' ? (
                          <img
                            src={getFileUrl(file.fileName)}
                            alt={file.originalFileName}
                            className="w-full h-24 object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22%3E%3Crect fill=%22%23ccc%22 width=%22100%22 height=%22100%22/%3E%3C/svg%3E';
                            }}
                          />
                        ) : (
                          <div className="w-full h-24 flex items-center justify-center bg-gray-300">
                            <span className="text-sm text-gray-600">Video</span>
                          </div>
                        )}
                        <a
                          href={getFileUrl(file.fileName)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                        >
                          <Eye className="w-6 h-6 text-white" />
                        </a>
                      </div>
                    ))}
                  </div>

                  <p className="text-sm text-gray-600">
                    {submission.files.length} file{submission.files.length !== 1 ? 's' : ''}
                  </p>

                  {/* Expanded Details */}
                  {expandedSubmission === submission._id && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="font-semibold text-gray-900 mb-2">File Details</h4>
                      <div className="space-y-2">
                        {submission.files.map((file, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between bg-gray-50 p-3 rounded border border-gray-200"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-900 truncate font-medium">
                                {file.originalFileName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {file.fileType === 'image' ? '🖼️ Image' : '🎥 Video'} •{' '}
                                {formatFileSize(file.fileSize)}
                              </p>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <a
                                href={getFileUrl(file.fileName)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded transition"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </a>
                              <a
                                href={`/api/file/${file.fileName}`}
                                download
                                className="p-2 text-green-600 hover:bg-green-50 rounded transition"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>

                      {submission.description && (
                        <div className="mt-4">
                          <h4 className="font-semibold text-gray-900 mb-1">Description</h4>
                          <p className="text-sm text-gray-700">{submission.description}</p>
                        </div>
                      )}

                      {submission.tags.length > 0 && (
                        <div className="mt-4">
                          <h4 className="font-semibold text-gray-900 mb-2">Tags</h4>
                          <div className="flex flex-wrap gap-2">
                            {submission.tags.map((tag, idx) => (
                              <span
                                key={idx}
                                className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full"
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Auto-refresh indicator */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Auto-refreshing every 10 seconds</p>
        </div>
        </>
        )}

        {/* Photos Tab */}
        {activeTab === 'photos' && (
          <>
            {/* Album Selector */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Select Album</h2>
              <div className="grid grid-cols-3 gap-4">
                {(['Games', 'Memories', 'Nature'] as AlbumName[]).map((album) => (
                  <button
                    key={album}
                    onClick={() => setSelectedAlbum(album)}
                    className={`p-4 rounded-lg border-2 transition ${
                      selectedAlbum === album
                        ? 'border-blue-600 bg-blue-50 text-blue-900'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <ImageIcon className="w-8 h-8 mx-auto mb-2" />
                    <div className="font-semibold">{album}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      {albumPhotos[album].length} photo(s)
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Upload Section */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Upload Photos to {selectedAlbum}
              </h2>
              <div className="flex items-center gap-4">
                <label
                  htmlFor="photo-upload"
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg cursor-pointer transition ${
                    uploadingPhotos
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  <Upload className="w-5 h-5" />
                  {uploadingPhotos ? 'Uploading...' : 'Select Photos'}
                </label>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  disabled={uploadingPhotos}
                  className="hidden"
                />
                <p className="text-sm text-gray-600">
                  Select one or more images to upload
                </p>
              </div>
            </div>

            {/* Photos Grid */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {selectedAlbum} Photos ({albumPhotos[selectedAlbum].length})
              </h2>
              
              {albumPhotos[selectedAlbum].length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>No photos in this album yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {albumPhotos[selectedAlbum].map((photo) => (
                    <div
                      key={photo.fileName}
                      className="relative group rounded-lg overflow-hidden bg-gray-100 border border-gray-200"
                    >
                      <img
                        src={`/images/${selectedAlbum}/${photo.fileName}`}
                        alt={photo.fileName}
                        className="w-full h-32 object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22%3E%3Crect fill=%22%23ccc%22 width=%22100%22 height=%22100%22/%3E%3C/svg%3E';
                        }}
                      />
                      
                      {/* Overlay with actions */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="flex gap-2">
                          <a
                            href={`/images/${selectedAlbum}/${photo.fileName}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white rounded-full hover:bg-gray-100 transition"
                            title="View"
                          >
                            <Eye className="w-4 h-4 text-gray-700" />
                          </a>
                          <button
                            onClick={() => handlePhotoDelete(selectedAlbum, photo.fileName)}
                            disabled={deletingPhoto === photo.fileName}
                            className="p-2 bg-red-600 rounded-full hover:bg-red-700 transition disabled:opacity-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      </div>

                      {/* File info */}
                      <div className="p-2 bg-white">
                        <p className="text-xs text-gray-600 truncate" title={photo.fileName}>
                          {photo.fileName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(photo.fileSize)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Auto-refresh indicator - Only for photos tab */}
        {activeTab === 'photos' && (
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>Auto-refreshing every 10 seconds</p>
          </div>
        )}
      </div>
    </div>
  );
}
